import type { StorefrontApiClient } from '@shopify/storefront-api-client';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  cartByIdQuery,
  cartCreateMutation,
  cartDiscountCodesUpdateMutation,
  cartLinesAddMutation,
  cartLinesRemoveMutation,
  cartLinesUpdateMutation
} from '../storefront-api-client-queries/queries';
import type { Cart, LastItemAdded } from '../types/types';
import { isDeletedProductLine } from '../utils/cart';
import { storage } from '../utils/local-storage-service';
import { getQueryParams } from '../utils/query-params';
import { dataOrThrow, storefrontClient } from '../utils/storefront-client';

interface ShopifyStoreState {
  /** `null` until the cart is loaded in the browser (or if loading it failed). */
  cart: Cart | null;
  adding: boolean;
  updating: boolean;
  loading: boolean;
  /** Ids (Shopify GIDs) of the favourite products. */
  favourites: string[];
  /** `true` until the favourites are read from localStorage in the browser. */
  favouritesLoading: boolean;
  lastItemAdded: LastItemAdded | null;
}

interface ShopifyStoreContextValue {
  client: StorefrontApiClient;
  state: ShopifyStoreState;
  addLineItem: (variantId: string, quantity?: number) => Promise<void>;
  updateLineItem: (lineItemId: string, quantity: number) => Promise<void>;
  removeLineItem: (lineItemId: string) => Promise<void>;
  addDiscount: (discountCode: string) => Promise<void>;
  removeDiscount: () => Promise<void>;
  toggleProductInFavourites: (productId: string) => void;
  setLastItemAdded: (item: LastItemAdded) => void;
  resetLastItemAdded: () => void;
}

interface CartMutationPayload {
  cart?: { id: string } | null;
  userErrors: Array<{ message: string }>;
}

// Global state
const ShopifyStoreContext = createContext<ShopifyStoreContextValue | null>(
  null
);

// The site is statically generated (no server), so the cart lives entirely in the browser:
// the cart id is kept in localStorage and the Storefront API is called directly (see utils/storefront-client.ts).
const client = storefrontClient;

const cartIdKey = 'cartId';
const favouritesStorageKey = 'pixwristFavourites';

const initialState: ShopifyStoreState = {
  cart: null,
  adding: false,
  updating: false,
  loading: true,
  // favourites are read from localStorage after hydration, so the prerendered HTML and the first client render match
  favourites: [],
  favouritesLoading: true,
  lastItemAdded: null
};

async function getCartById(cartId: string) {
  const { cart } = dataOrThrow(
    await client.request(cartByIdQuery, { variables: { id: cartId } })
  );

  // null if Shopify no longer has the cart (e.g. expired or it became an order)
  return cart ?? null;
}

async function createCart() {
  const cart = dataOrThrow(await client.request(cartCreateMutation)).cartCreate
    ?.cart;

  if (!cart) {
    throw new Error('Shopify returned no cart on create.');
  }

  const newCart = await getCartById(cart.id);

  if (!newCart) {
    throw new Error(`Newly created cart not found: ${cart.id}`);
  }

  return newCart;
}

/** Throws on GraphQL or user errors, otherwise returns the fresh state of the mutated cart. */
async function getCartAfterMutation(
  payload: CartMutationPayload | null | undefined,
  errors: { message?: string } | undefined
) {
  if (errors) {
    throw new Error(errors.message);
  }

  if (payload?.userErrors.length) {
    throw new Error(payload.userErrors.map(e => e.message).join('\n'));
  }

  if (!payload?.cart) {
    throw new Error('Shopify returned no cart after mutation.');
  }

  // TODO: the mutations could return the whole cart, so there would be no need to get it by id afterwards.
  return getCartById(payload.cart.id);
}

export function ShopifyStoreProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState(initialState);
  const discountFromQueryParamChecked = useRef(false);

  const removeCart = () => storage.remove(cartIdKey);

  const setCart = (cart: Cart | null) => {
    setState(prevState => ({ ...prevState, cart }));
  };

  const initCart = async () => {
    try {
      const storedCartId = storage.get<string>(cartIdKey);
      let cart =
        storedCartId?.includes('gid://') ?
          await getCartById(storedCartId)
        : null;

      // A new cart is needed when there is none yet, or the stored one no longer exists
      // (the Storefront API does not return a cart that became an order).
      // DEPRECATED: redirect to checkout success page with Shopify's additional scripts no longer works
      // WORKAROUND: the Storefront API Cart does not maintain the state of a completed cart that became an order
      if (!cart || window.location.pathname.includes('checkout-success')) {
        cart = await createCart();
      }

      // remove lines of deleted products from the existing cart
      const deletedLineIds = cart.lines.edges
        .map(edge => edge.node)
        .filter(isDeletedProductLine)
        .map(line => line.id);

      if (deletedLineIds.length) {
        const { data, errors } = await client.request(cartLinesRemoveMutation, {
          variables: { cartId: cart.id, lineIds: deletedLineIds }
        });
        cart =
          (await getCartAfterMutation(data?.cartLinesRemove, errors)) ?? cart;
      }

      storage.set(cartIdKey, cart.id);

      setState(prevState => ({ ...prevState, cart, loading: false }));
    } catch (error) {
      console.error('Failed to initialize the cart', error);
      removeCart();
      setState(prevState => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      favourites: storage.get<string[]>(favouritesStorageKey) ?? [],
      favouritesLoading: false
    }));

    initCart();

    // The browser can restore a page from its back/forward cache, e.g. when going back from the Shopify checkout:
    // the page is shown as it was left, so the cart is checked again (it no longer exists if it became an order).
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        initCart();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (state.cart?.id && !discountFromQueryParamChecked.current) {
      // check if discount code is in query param and apply it (once per page load)
      discountFromQueryParamChecked.current = true;
      checkDiscountInQueryParam(state.cart);
    }
  }, [state.cart]);

  const getCartId = () => {
    if (!state.cart) {
      throw new Error('Cart is not initialized.');
    }

    return state.cart.id;
  };

  const addLineItem = async (merchandiseId: string, quantity: number = 1) => {
    setState(prevState => ({ ...prevState, adding: true }));

    try {
      const { data, errors } = await client.request(cartLinesAddMutation, {
        variables: {
          cartId: getCartId(),
          lines: [{ merchandiseId, quantity }]
        }
      });
      setCart(await getCartAfterMutation(data?.cartLinesAdd, errors));
    } finally {
      setState(prevState => ({ ...prevState, adding: false }));
    }
  };

  const removeLineItem = async (lineId: string) => {
    const { data, errors } = await client.request(cartLinesRemoveMutation, {
      variables: {
        cartId: getCartId(),
        lineIds: [lineId]
      }
    });
    setCart(await getCartAfterMutation(data?.cartLinesRemove, errors));
  };

  const updateLineItem = async (lineId: string, quantity: number = 1) => {
    setState(prevState => ({ ...prevState, updating: true }));

    try {
      const { data, errors } = await client.request(cartLinesUpdateMutation, {
        variables: {
          cartId: getCartId(),
          lines: [{ id: lineId, quantity }]
        }
      });
      setCart(await getCartAfterMutation(data?.cartLinesUpdate, errors));
    } finally {
      setState(prevState => ({ ...prevState, updating: false }));
    }
  };

  const updateDiscountCodes = async (discountCodes: string[]) => {
    const { data, errors } = await client.request(
      cartDiscountCodesUpdateMutation,
      {
        variables: {
          cartId: getCartId(),
          discountCodes
        }
      }
    );
    setCart(await getCartAfterMutation(data?.cartDiscountCodesUpdate, errors));
  };

  const addDiscount = (discountCode: string) =>
    updateDiscountCodes([discountCode]);

  const removeDiscount = () => updateDiscountCodes([]);

  const toggleProductInFavourites = (productId: string) => {
    setState(prevState => {
      const favourites =
        prevState.favourites.includes(productId) ?
          prevState.favourites.filter(id => id !== productId)
        : [...prevState.favourites, productId];

      storage.set(favouritesStorageKey, favourites);

      return { ...prevState, favourites };
    });
  };

  const setLastItemAdded = (item: LastItemAdded) => {
    setState(prevState => ({
      ...prevState,
      adding: false,
      lastItemAdded: item
    }));
  };

  const resetLastItemAdded = () => {
    setState(prevState => ({
      ...prevState,
      adding: false,
      lastItemAdded: null
    }));
  };

  const checkDiscountInQueryParam = (cart: Cart) => {
    const discountCode = getQueryParams().get('discount');
    const isCodeAlreadyInCart = cart.discountCodes.some(
      c => c.code.toLowerCase() === discountCode?.toLowerCase()
    );

    // NOTE: a code stays on the cart even if it is not applicable yet (e.g. the cart is empty),
    // Shopify applies it automatically once the cart becomes eligible.
    if (
      discountCode &&
      !cart.discountAllocations.length &&
      !isCodeAlreadyInCart
    ) {
      console.log('Applying discount from query param:', discountCode);
      addDiscount(discountCode).catch(error =>
        console.error('Failed to apply discount from query param', error)
      );
    }
  };

  return (
    <ShopifyStoreContext
      value={{
        client,
        state,
        addLineItem,
        updateLineItem,
        removeLineItem,
        addDiscount,
        removeDiscount,
        toggleProductInFavourites,
        setLastItemAdded,
        resetLastItemAdded
      }}
    >
      {children}
    </ShopifyStoreContext>
  );
}

export function useShopifyStore() {
  const context = useContext(ShopifyStoreContext);

  if (!context) {
    throw new Error(
      'useShopifyStore must be used within a ShopifyStoreProvider'
    );
  }

  return context;
}
